/* istanbul ignore file */
import { Navigate, Routes } from 'react-router-dom';
import SalesRouter from 'domain/sales/SalesRouter';
import { AuditRouter } from 'domain/audit';
import { CoreServiceRouter } from 'domain/coreService';
import { OneStopRouter, RedirectToOneStop } from 'domain/gpo/oneStop';
import { MyActivityRouter } from 'domain/people/employeePortal';
import { HandbookRouter } from 'domain/gpo/handbook';
import { ArtifactsRouter } from 'domain/documents/artifact';
import { ExternalFormRouter } from 'domain/gpo/externalForm';
import { SendEmailRouter } from 'infrastructure/email/sendEmail';
import { UserSettingsRouter } from 'settings';
import ArtifactStorageRouter from 'domain/documents/artifactStorage/ArtifactStorageRouter';
import { Route } from 'framework/routing';
import { Page404, Page403, Page451, PageErrorBoundaryCrash } from 'framework/statusPages';
import useAuthenticationService from 'infrastructure/security/authentication/authenticationService';
import Page from './layout/Default';
import { ROUTE403, ROUTE404, ROUTE451 } from 'framework/api';
import { InstantFeedbackRouter } from 'domain/people/feedback/instantFeedback';
import CandidateContextExtensionRouter from 'domain/recruiting/extension/CandidateContextExtensionRouter';
import {
  INSTANT_FEEDBACK_PATH,
  OLD_INSTANT_FEEDBACK_PATH,
} from 'domain/people/feedback/instantFeedback/InstanFeedbackRouter';
import ComplianceRouter from 'domain/compliance/ComplianceRouter';
import { DocumentsRouter } from 'domain/documents';
import TimeTrackRouter from 'domain/gpo/oneStop/workloadReportingIssues/TimeTrackRouter';
import PublicInterviewSchedulingRouter from 'domain/recruiting/publicInterviewScheduling/PublicInterviewSchedulingRouter';
import GpoRouter from 'domain/gpo/GpoRouter';
import PeopleRouter from 'domain/people/PeopleRouter';
import RecruitingRouter from 'domain/recruiting/RecruitingRouter';
import SidepanelExtensionRouter from 'domain/recruiting/candidate/applications/SidepanelExtensionRouter';
import FinanceRouter from 'domain/finance/FinanceRouter';
import StaffingRouter from 'domain/staffing/StaffingRouter';
import ProjectsRouter from 'domain/projects/ProjectsRouter';
import OrganizationRouter from 'domain/organization/OrganizationRouter';
import { PrivilegedRouter } from 'domain/security/privilegedAccess';
import { AuthenticationRouter } from 'infrastructure/security/authentication';
import PublicLayout from './layout/Public';
import { SkillBasedQueueRouter } from 'domain/gpo/skillBasedQueue';
import { SleepConsoleRouter } from 'domain/platform/sleepConsole';

const ERROR_BOUNDARY_TEST_ROUTE = '/__test/error-boundary-crash';

const ApplicationRouter = () => {
  const { isAuthenticated } = useAuthenticationService();

  return (
    <Routes>
      {/* Extension */}
      <Route path={SidepanelExtensionRouter.getRouterPath()} element={<SidepanelExtensionRouter />} />
      <Route path={CandidateContextExtensionRouter.getRouterPath()} element={<CandidateContextExtensionRouter />} />,
      {/* Public pages */}
      <Route
        path={ExternalFormRouter.getRouterPath()}
        element={
          <PublicLayout sx={{ py: 2 }}>
            <ExternalFormRouter />
          </PublicLayout>
        }
      />
      {/* TODO Delete after some time */}
      <Route path={PublicInterviewSchedulingRouter.getRouterPath()} element={<PublicInterviewSchedulingRouter />} />
      <Route path={ROUTE403} element={<Page403 />} />
      <Route path={ROUTE451} element={<Page451 />} />
      <Route path={ROUTE404} element={<Page404 isAuthenticated={isAuthenticated} />} />
      <Route element={<Page />}>
        {/* Base routes */}
        <Route path="/" element={<RedirectToOneStop />} />
        {AuthenticationRouter()}
        {/* Extension */}
        <Route path={SidepanelExtensionRouter.getRouterPath()} element={<SidepanelExtensionRouter />} />
        {/* App */}
        {PeopleRouter()}
        {RecruitingRouter()}
        {FinanceRouter()}
        {StaffingRouter()}
        {ProjectsRouter()}
        {OrganizationRouter()}
        <Route path={SalesRouter.getRouterPath()} element={<SalesRouter />} />
        <Route path={ComplianceRouter.getRouterPath()} element={<ComplianceRouter />} />
        <Route path={AuditRouter.getRouterPath()} element={<AuditRouter />} />
        {/* Direct link only, not listed in any menu; CTO only (the core API decides, see CoreServiceAccessGuard) */}
        <Route path={CoreServiceRouter.getRouterPath()} element={<CoreServiceRouter />} />
        <Route path={PrivilegedRouter.getRouterPath()} element={<PrivilegedRouter />} />
        <Route path={OneStopRouter.getRouterPath()} element={<OneStopRouter />} />
        <Route path={MyActivityRouter.getRouterPath()} element={<MyActivityRouter />} />
        <Route path={GpoRouter.getRouterPath()} element={<GpoRouter />} />
        <Route path={ArtifactsRouter.getRouterPath()} element={<ArtifactsRouter />} />
        <Route path={SendEmailRouter.getRouterPath()} element={<SendEmailRouter />} />
        <Route path={UserSettingsRouter.getRouterPath()} element={<UserSettingsRouter />} />
        <Route path={HandbookRouter.getRouterPath()} element={<HandbookRouter />} />
        <Route path={ArtifactStorageRouter.getRouterPath()} element={<ArtifactStorageRouter />} />
        <Route path={DocumentsRouter.getRouterPath()} element={<DocumentsRouter />} />
        <Route path={InstantFeedbackRouter.getRouterPath()} element={<InstantFeedbackRouter />} />
        <Route path={SkillBasedQueueRouter.getRouterPath()} element={<SkillBasedQueueRouter />} />,
        <Route
          path={`${OLD_INSTANT_FEEDBACK_PATH}/*`}
          element={<Navigate to={`${INSTANT_FEEDBACK_PATH}/add`} replace />}
        />
        <Route path={TimeTrackRouter.getRouterPath()} element={<TimeTrackRouter />} />
        <Route path={SleepConsoleRouter.getRouterPath()} element={<SleepConsoleRouter />} />
        {process.env.NODE_ENV !== 'production' && (
          <Route path={ERROR_BOUNDARY_TEST_ROUTE} element={<PageErrorBoundaryCrash />} />
        )}
        <Route path={ROUTE404} element={<Page404 isAuthenticated={isAuthenticated} />} />
        <Route path="*" element={<Page404 isAuthenticated={isAuthenticated} />} />
      </Route>
    </Routes>
  );
};

export default ApplicationRouter;
